import { Injectable } from '@angular/core';
import { TSTreeNode } from 'tsui';
import { FeatureFlag, NewPermission, Permission } from '../../core/enums';
import { Store } from '@ngxs/store';
import { AccountState } from '../../state/account/state';
import { UserState } from '@state/user/state';

interface TSTreeNodeWithMetaData extends TSTreeNode {
  children?: TSTreeNodeWithMetaData[];
  permission?: Permission | NewPermission;
  featureFlag?: FeatureFlag;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  private readonly _settingsOptions: TSTreeNodeWithMetaData[] = [
    {
      id: 1,
      name: 'General',
      icon: 'ts_cog',
      link: 'settings/general',
      expandable: false,
      permission: Permission.SETTINGS_ACCESS,
    },
    {
      id: 2,
      name: 'User Management',
      icon: 'ts_users-alt',
      expandable: true,
      hideAddNew: true,
      children: [
        {
          id: 1,
          name: 'Users',
          icon: 'ts_users-alt',
          link: 'settings/users',
          expandable: false,
          permission: Permission.SETTINGS_USERS,
        },
        {
          id: 2,
          name: 'Teams',
          icon: 'ts_sitemap',
          link: 'settings/teams',
          expandable: false,
          permission: NewPermission.SETTINGS_MANAGE_TEAMS,
          featureFlag: FeatureFlag.NEW_PERMISSIONS,
        },
        {
          id: 3,
          name: 'Permissions',
          icon: 'ts_shield',
          link: 'settings/roles',
          expandable: false,
          permission: Permission.SETTINGS_PERMISSIONS,
        },
        {
          id: 4,
          name: 'Roles',
          icon: 'ts_user-check',
          link: 'settings/task-roles',
          expandable: false,
          permission: Permission.SETTINGS_TASK_ROLES,
        },
      ],
    },
    {
      id: 3,
      name: 'Asset Management',
      icon: '',
      expandable: true,
      hideAddNew: true,
      children: [
        {
          id: 1,
          name: 'Chart of Accounts',
          icon: 'ts_list-ui-alt',
          link: 'settings/chart-of-accounts',
          expandable: false,
          featureFlag: FeatureFlag.ASSET_MANAGEMENT,
        },
        {
          id: 2,
          name: 'Accounts Mapping',
          icon: 'ts_sitemap',
          link: 'settings/accounts-mapping',
          expandable: false,
          featureFlag: FeatureFlag.ASSET_MANAGEMENT,
        },
      ],
    },
    {
      id: 4,
      name: 'Dropdowns',
      icon: 'ts_angle-double-down',
      link: 'settings/customizations',
      expandable: false,
      permission: Permission.SETTINGS_DROPDOWNS,
    },
    {
      id: 5,
      name: 'Fields',
      icon: 'ts_text',
      link: 'settings/custom_fields',
      expandable: false,
      permission: Permission.SETTINGS_FIELDS,
    },
    {
      id: 6,
      name: 'Task Templates',
      icon: 'ts_task-gantt',
      link: 'settings/task-lists',
      expandable: false,
      permission: Permission.SETTINGS_TASK_TEMPLATES,
    },
    {
      id: 8,
      name: 'Sheets',
      icon: 'ts_table',
      link: 'settings/sheets',
      expandable: false,
      permission: Permission.SETTINGS_SHEETS,
    },
    {
      id: 9,
      name: 'Apps',
      icon: 'ts_apps',
      link: 'settings/integrations',
      expandable: false,
      permission: Permission.SETTINGS_APPS,
    },
    {
      id: 10,
      name: 'Code',
      icon: 'ts_brackets-curly',
      link: 'settings/functions',
      expandable: false,
      permission: Permission.SETTINGS_CODE,
    },
    {
      id: 11,
      name: 'Imports',
      icon: 'ts_import',
      link: 'settings/imports',
      expandable: false,
      permission: Permission.SETTINGS_IMPORTS,
    },
    {
      id: 12,
      name: 'Layouts',
      icon: 'ts_web-grid-alt',
      link: 'settings/layouts',
      expandable: false,
      permission: this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS))
        ? NewPermission.SETTINGS_MANAGE_LAYOUTS
        : Permission.PROJECT_DASHBOARD_EDIT_LAYOUT,
    },
    {
      id: 13,
      name: 'Automations',
      icon: 'ts_robot',
      link: 'settings/automations',
      expandable: false,
      permission: Permission.SETTINGS_AUTOMATIONS,
      featureFlag: FeatureFlag.AUTOMATIONS,
    },
    {
      id: 14,
      name: 'Objects Manager',
      icon: 'ts_cloud-database-tree',
      link: 'settings/objects_manager',
      permission: Permission.SETTINGS_OBJECT_MANAGER,
      featureFlag: FeatureFlag.CUSTOM_OBJECTS,
    },
    {
      id: 15,
      name: 'Hooks',
      icon: 'ts_robot',
      link: 'settings/hooks',
      permission: Permission.SETTINGS_ACCESS,
      featureFlag: FeatureFlag.ACCOUNT_HOOKS,
    },
    {
      id: 16,
      name: 'SAML/SSO',
      icon: 'ts_users-alt',
      link: 'settings/saml',
      permission: Permission.SETTINGS_ACCESS,
    },
    {
      id: 17,
      name: 'Audit',
      icon: 'ts_shield-check',
      link: 'settings/audit',
      permission: NewPermission.SETTINGS_MANAGE_AUDIT,
    },
  ];

  get settingsOptions(): TSTreeNode[] {
    const filterByPermission = ({ permission, id }: TSTreeNodeWithMetaData) => {
      if (id === 15) {
        const user = this.store.selectSnapshot(UserState.getUser);
        return user?.email?.includes('@termsheet.com');
      }
      if (!permission) return true;

      return !!this.store.selectSnapshot(UserState.hasPermission(permission));
    };

    const filterByFeatureFlag = ({ featureFlag }: TSTreeNodeWithMetaData) => {
      const hasFeatureFlag = !!featureFlag;

      // if a feature flag doesn't exist for the menu option, it can be shown
      if (!hasFeatureFlag) return true;

      return this.store.selectSnapshot(AccountState.isFeatureEnabled(featureFlag));
    };

    return (
      this._settingsOptions
        // filters top level options
        .filter(filterByPermission)
        .filter(filterByFeatureFlag)
        // filters children options
        .reduce((acc, treeNode) => {
          if (treeNode?.children && Array.isArray(treeNode?.children)) {
            const children = treeNode.children.filter(filterByPermission).filter(filterByFeatureFlag);

            return children.length > 0 ? [...acc, { ...treeNode, children }] : [...acc];
          }

          return [...acc, treeNode];
        }, [])
    );
  }

  constructor(private store: Store) {}
}
